import {
	BatchStatus,
	SystemAddOn,
	Severity,
} from "node-library/dist/constants";
import { formatCustomDate } from "node-library/dist/helpers/date_helper";
import {
	FrontendBatchType,
	FrontendCompanyTestCategory,
	FrontendCompanyWithAdditionalInfomation,
	FrontendSampleSpecification,
	FrontendSampleSpecificationDirectory,
	FrontendUserInfo,
} from "node-library/dist/portal";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { Card, Divider, Tab, Tabs } from "@mui/material";
import { NeutralColor, PrimaryColor } from "node-library/dist/colors";
import {
	BlackButton,
	CustomIconButton,
	DropdownButton,
	GreenButton,
} from "../../../common-components/button";
import { NavBar } from "../../../common-components/navbar";
import { EditIcon, UserIcon } from "../../../common-components/svgIcons";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { getContentContainer } from "../../../helpers/containerHelper";

import { FrontendBatchPageTestBatchDetails } from "node-library/dist/structures/frontendSampleTestBatchPageDefinitions";
import {
	findCompanyBatchTypeById,
	getBatchStatusColors,
	getBatchStatusTranslationString,
	getDefaultOtherTestBatchType,
} from "../../../helpers/testBatchHelper";
import BatchInstancesTable from "./components/batchInstancesTable";
import EditBatchStatusModal from "./components/editBatchStatusModal";
import { sendPOSTRequest } from "../../../requestHelper/sendRequest";
import { GreenTickIcon } from "../../../common-components/toast/successIcons";
import { RedErrorIcon } from "../../../common-components/toast/errorIcons";
import WorkflowSection from "./workflowSection";
import {
	hasPermission,
	PermissionMap,
	Permissions,
} from "node-library/dist/permissions";
import EditAssignedUsersModal from "../../../common-components/modals/editAssignedUsersModal";
import WorkflowCommentCard from "./components/workflowComponents/workflowCommentCard";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
	user: FrontendUserInfo;
	batchDetails: FrontendBatchPageTestBatchDetails;
	setBatchDetails: React.Dispatch<
		React.SetStateAction<FrontendBatchPageTestBatchDetails | undefined>
	>;

	goBackToPreviousPage: (reload: boolean) => void;
	reloadBatchDetails: () => void;
	specDirectories: Array<FrontendSampleSpecificationDirectory>;
	updateSpecification: (spec: FrontendSampleSpecification) => void;
}

export default function WorkflowManagementBatchDetailsContent(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		batchDetails,
		setBatchDetails,
		reloadBatchDetails,
		goBackToPreviousPage,
		user,
		company,
		setCompany,
		specDirectories,
		updateSpecification,
	} = props;

	const [selectedTab, setSelectedTab] = useState<number>(0);
	//order updatable fields
	const [batchStatus, setBatchStatus] = useState<BatchStatus>(
		batchDetails.batchStatus,
	);

	const [createdTime, setCreatedTime] = useState<Date | undefined>(
		batchDetails.inserted ? new Date(batchDetails.inserted) : undefined,
	);
	const [loading, setLoading] = useState<boolean>(false);

	const dueDate: Date = batchDetails.dueDate as Date;

	const [resultSubmitTime, setResultSubmitTime] = useState<Date | undefined>(
		batchDetails.resultSubmitTime
			? new Date(batchDetails.resultSubmitTime)
			: undefined,
	);
	const [completeTime, setCompleteTime] = useState<Date | undefined>(
		batchDetails.completeTime ? new Date(batchDetails.completeTime) : undefined,
	);

	const [comments, setComments] = useState<string>(batchDetails.comments || "");

	const [editAssignedUsersModalOpen, setEditAssignedUsersModalOpen] =
		useState<boolean>(false);

	const userHasEditPermission: boolean = hasPermission(
		user.role?.permissions as PermissionMap,
		Permissions.batches.getDetails,
	);
	const batchEditDisabled: boolean = batchDetails.editDisabled;

	const testCategory: FrontendCompanyTestCategory =
		company.companyTestCategories.find((category) => {
			return category.testCategoryId == batchDetails.mainTestCategoryId;
		}) as FrontendCompanyTestCategory;
	const isOtherTestBatch: boolean = batchDetails.isOtherTestBatch;

	const batchType: FrontendBatchType = isOtherTestBatch
		? getDefaultOtherTestBatchType(testCategory.testCategoryId, translation)
		: (findCompanyBatchTypeById(
				company,
				testCategory.testCategoryId,
				batchDetails.batchTypeId || "",
			) as FrontendBatchType);

	let statusString: string;
	let statusColor: {
		backgroundColor: string;
		borderColor: string;
		textColor: string;
	};
	let statusDescriptionString: string;

	statusString = getBatchStatusTranslationString(batchStatus, translation);
	statusColor = getBatchStatusColors(batchStatus);

	const batchCreationDate: string = createdTime
		? formatCustomDate(createdTime)
		: "";

	const navbarItems: Array<{
		label: string;
		onClick: () => void;
		isCurrentItem: boolean;
	}> = [
		{
			label: translation("Batches"),
			onClick: () => goBackToPreviousPage(false),
			isCurrentItem: false,
		},
		{
			label: translation("Details"),
			onClick: () => {},
			isCurrentItem: true,
		},
	];

	let actionButtonOptions: Array<{
		key: string;
		onClick: () => void;
		icon: React.ReactNode;
		text: string;
		widthToShow?: number;
	}> = userHasEditPermission
		? [
				...(!batchEditDisabled
					? [
							{
								key: "edit-batch",
								onClick: () => {
									console.log("asdf");
								},
								icon: (
									<EditIcon
										strokeColor={NeutralColor.NEUTRAL_GREY_950}
									></EditIcon>
								),
								text: translation("EditBatch"),
							},
						]
					: []),
				...[
					{
						key: "user-assignments",
						onClick: () => {
							setEditAssignedUsersModalOpen(true);
						},
						icon: (
							<UserIcon strokeColor={NeutralColor.NEUTRAL_GREY_950}></UserIcon>
						),
						text: translation("UserAssignments"),
					},
				],
			]
		: [];
	let keyActionOption: {
		key: string;
		onClick: () => void;
		children: any;
		disabled?: boolean;
	} = {
		key: "start-batch",
		onClick: () => {
			startBatch();
		},
		children: translation("StartBatch"),
	};

	async function startBatch() {
		try {
			setLoading(true);

			const data = await sendPOSTRequest("/sample-test-batches/start-batch", {
				batchId: batchDetails.testBatchId,
			});

			const stepId: string = data.stepId;
			setBatchStatus(BatchStatus.IN_PROGRESS);
			setBatchDetails((prev) => ({
				...(prev as FrontendBatchPageTestBatchDetails),
				currentWorkflowStepId: stepId,
			}));
			openSnackbar(
				translation("BatchStatusUpdated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />,
			);
		} catch (err: any) {
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />,
			);
		} finally {
			setLoading(false);
		}
	}

	let actionButton: React.ReactNode = <></>;
	if (actionButtonOptions.length == 1) {
		actionButton = (
			<BlackButton
				id={"order-details-actionbutton"}
				buttonClassName={
					actionButtonOptions[0].widthToShow
						? "order-details-inprogress-action-button"
						: ""
				}
				onClick={(e) => {
					actionButtonOptions[0].onClick();
				}}
			>
				{actionButtonOptions[0].text}
			</BlackButton>
		);
	} else if (actionButtonOptions.length > 1) {
		actionButton = (
			<DropdownButton
				buttonColor="black"
				id={"order-details-actionbutton"}
				container={getContentContainer()}
				menuOptions={actionButtonOptions.map((option) => {
					return {
						...option,
						children: (
							<div className="icon-dropdown-menu-item">
								{option.icon}
								{option.text}
							</div>
						),
					};
				})}
				onClick={(e) => {}}
			>
				{translation("Actions")}
			</DropdownButton>
		);
	}

	const buttonPanel: React.ReactNode = (
		<div
			style={{
				display: "flex",
				gap: "5px",
				justifyContent: "center",
				flexWrap: "wrap",
			}}
		>
			{actionButton}

			<GreenButton
				id={"order-details-key-action-button"}
				onClick={keyActionOption.onClick}
				disabled={keyActionOption.disabled}
			>
				{keyActionOption.children}
			</GreenButton>
		</div>
	);

	const batchDetailsHeaderRow: React.ReactNode = (
		<div
			style={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				gap: "10px",
				flexWrap: "wrap",
			}}
		>
			<div>
				<div style={{ marginLeft: "35px" }}>
					<NavBar items={navbarItems}></NavBar>
				</div>

				<div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
					<CustomIconButton
						id="order-details-back-button"
						size="small"
						onClick={() => {
							goBackToPreviousPage(false);
						}}
						icon={<KeyboardArrowLeftIcon />}
					></CustomIconButton>

					<div
						className="typography-heading-01"
						style={{ display: "flex", alignItems: "center", gap: "5px" }}
					>
						{`${translation("Batch")} ${batchDetails.batchFullCode}`}
					</div>
				</div>
			</div>
			{buttonPanel}
		</div>
	);

	const orderDetailsCardRow: React.ReactNode = (
		<div className="instance-details-card-row">
			<Card className="card-border-radius-14p card-medium-padding">
				<div className="order-details-header-card-content">
					<div>
						<div
							style={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								gap: "10px",
							}}
						>
							<div
								className="typography-body-01 typography-bold admin-sample-status-box"
								style={{
									backgroundColor: statusColor.backgroundColor,
									border: `1px solid ${statusColor.borderColor}`,
									color: statusColor.textColor,
									minWidth: "20%",
								}}
							>
								{statusString}
							</div>
						</div>
						{/* <div
							className="typography-caption"
							style={{ color: NeutralColor.NEUTRAL_GREY_500, marginTop: "5px" }}
						>
							{statusDescriptionString}
						</div> */}
					</div>
					<div>
						<div className="order-details-time-grid">
							<div className="order-details-time-grid-item">
								<div
									className="typography-caption"
									style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
								>
									{translation("BatchCreatedOn")}
								</div>
								<div className="typography-body-01 typography-bold">
									{batchCreationDate ? batchCreationDate.split(" | ")[0] : "--"}
								</div>
								<div className="typography-caption">
									{batchCreationDate ? batchCreationDate.split(" | ")[1] : ""}
								</div>
							</div>
							<Divider orientation="vertical" flexItem></Divider>

							<div className="order-details-time-grid-item">
								<div
									className="typography-caption"
									style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
								>
									{translation("ResultSubmittedOn")}
								</div>
								<div
									className="typography-body-01 typography-bold"
									style={{
										color: NeutralColor.NEUTRAL_GREY_500,
									}}
								>
									{resultSubmitTime
										? formatCustomDate(resultSubmitTime).split(" | ")[0]
										: "--"}
								</div>
								<div className="typography-caption">
									{resultSubmitTime
										? formatCustomDate(resultSubmitTime).split(" | ")[1]
										: ""}
								</div>
							</div>
							<Divider orientation="vertical" flexItem></Divider>

							<div className="order-details-time-grid-item">
								<div
									className="typography-caption"
									style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
								>
									{translation("CompletedOn")}
								</div>
								<div
									className="typography-body-01 typography-bold"
									style={{
										color: completeTime ? PrimaryColor.PRIMARY_500 : "black",
									}}
								>
									{completeTime
										? formatCustomDate(completeTime).split(" | ")[0]
										: "--"}
								</div>
								<div className="typography-caption">
									{completeTime
										? formatCustomDate(completeTime).split(" | ")[1]
										: ""}
								</div>
							</div>
						</div>
					</div>
				</div>
			</Card>
			<Card className="card-border-radius-14p card-medium-padding">
				<div>
					<div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
						<div className="order-details-header-icon-container">
							<UserIcon
								strokeColor={PrimaryColor.PRIMARY_500}
								fillColor={PrimaryColor.PRIMARY_500}
							></UserIcon>
						</div>
						<div className="typography-body-01 typography-bold">
							{translation("BatchDetails")}
						</div>
					</div>

					<div
						style={{
							marginTop: "30px",
							display: "flex",
							flexDirection: "column",
							gap: "10px",
						}}
					>
						<div className="order-details-user-info-row typography-body-02">
							<div className="order-details-user-info-row-title">
								{translation("BatchId")}
							</div>
							<div className="order-details-info-row-info">
								{batchDetails.batchFullCode}
							</div>
						</div>
						<div className="order-details-user-info-row typography-body-02">
							<div className="order-details-user-info-row-title">
								{translation("PrimaryTestCategory")}
							</div>
							<div className="order-details-info-row-info">
								{testCategory.testCategoryName}
							</div>
						</div>

						<div className="order-details-user-info-row typography-body-02">
							<div className="order-details-user-info-row-title">
								{translation("BatchType")}
							</div>
							<div className="order-details-info-row-info">
								{isOtherTestBatch ? translation("Other") : batchType?.batchName}
							</div>
						</div>
						<div className="order-details-user-info-row typography-body-02">
							<div className="order-details-user-info-row-title">
								{translation("MethodId")}
							</div>
							<div className="order-details-info-row-info">
								{batchDetails.testMethodCode}
							</div>
						</div>
						<div className="order-details-user-info-row typography-body-02">
							<div className="order-details-user-info-row-title">
								{translation("BatchSize")}
							</div>
							<div className="order-details-info-row-info">
								{batchDetails.batchSize}
							</div>
						</div>
						<div className="order-details-user-info-row typography-body-02">
							<div className="order-details-user-info-row-title">
								{translation("CreatedBy")}
							</div>
							<div className="order-details-info-row-info">
								{`${batchDetails.createdBy.firstName} ${batchDetails.createdBy.lastName}`}
							</div>
						</div>
						<div className="order-details-user-info-row typography-body-02">
							<div className="order-details-user-info-row-title">
								{translation("AssignedTo")}
							</div>
							<div className="order-details-info-row-info">
								{batchDetails.assignedUsers
									.map((user) => {
										return `${user.firstName} ${user.lastName}`;
									})
									.join(", ")}
							</div>
						</div>
						<div className="order-details-user-info-row typography-body-02">
							<div className="order-details-user-info-row-title">
								{translation("Comments")}
							</div>
							<div className="order-details-info-row-info wrap-text">
								{comments || translation("None")}
							</div>
						</div>
					</div>
				</div>
			</Card>
		</div>
	);

	let viewSampleTable: JSX.Element = (
		<BatchInstancesTable
			sampleInstances={batchDetails.instances}
			company={company}
			setCompany={setCompany}
			user={user as FrontendUserInfo}
			batchType={batchType}
			batchVerifyingSampleId={batchDetails.duplicateInstanceId}
			specDirectories={specDirectories}
			updateSpecification={updateSpecification}
		/>
	);
	const workflowSection: JSX.Element = (
		<WorkflowSection
			user={user as FrontendUserInfo}
			sampleInstances={batchDetails.instances}
			company={company}
			batchDetails={batchDetails}
			batchType={batchType}
			batchVerifyingSampleId={batchDetails.duplicateInstanceId}
			reloadBatchDetails={reloadBatchDetails}
		/>
	);

	const handleChange = (event: React.SyntheticEvent, newValue: number) => {
		setSelectedTab(newValue);
	};

	const workflowCommentCard = (
		<WorkflowCommentCard
			company={company}
			user={user}
			batchDetails={batchDetails}
			setBatchDetails={setBatchDetails}
		></WorkflowCommentCard>
	);

	const instantTestBatchBody: React.ReactNode = (
		<div className="">
			<Tabs
				value={selectedTab}
				onChange={handleChange}
				aria-label="Instant Test Batch Tabs"
				variant="scrollable"
				scrollButtons="auto"
				sx={{
					"& .MuiTab-root": {
						cursor: "pointer",
					},
				}}
			>
				<Tab label={translation("Instances")} />
				<Tab label={translation("Workflow")} />
			</Tabs>
			<div style={{ marginTop: "10px" }}>
				{selectedTab === 0 && viewSampleTable}
				{selectedTab === 1 && workflowSection}
			</div>
		</div>
	);

	return (
		<div style={{ margin: "0 auto" }}>
			<div>
				{batchDetailsHeaderRow}
				{orderDetailsCardRow}
				{workflowCommentCard}

				{instantTestBatchBody}
			</div>
			{
				<EditAssignedUsersModal
					batchDetails={batchDetails}
					open={editAssignedUsersModalOpen}
					setOpen={setEditAssignedUsersModalOpen}
					setBatchDetails={setBatchDetails}
				/>
			}
		</div>
	);
}
